import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Card, Skeleton, Box, useTheme } from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import ChartFilter from "./Filter";
import OrderFilter from "./OrderFilter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const CameraFrameComparisonChart = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const theme = useTheme();

  const [filter, setFilter] = useState({
    start_date: "",
    end_date: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      const url = `analytics/cameras/frame-comparison`;

      const { data } = await axios.get(`${baseURL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      
      });

      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching camera frame data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Labels + Values
  const labels = dataList.map(item => item.camera_name);
  const values = dataList.map(item => item.frame_count);

  const avgFrame =
    values.length > 0
      ? values.reduce((acc, val) => acc + val, 0) / values.length
      : 0;

  const barColors = dataList.map(item => {
    if (item.comparison === "Above Average") return "rgba(46, 204, 113, 0.8)";
    if (item.comparison === "Below Average") return "rgba(231, 76, 60, 0.8)";
    return "rgba(52, 152, 219, 0.8)";
  });

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  const data = {
    labels,
    datasets: [
      {
        label: "Frame Count",
        data: values,
        backgroundColor: barColors,
      },
      {
        label: "Average Frame Count",
        data: Array(values.length).fill(avgFrame),
        borderColor: "rgba(105, 90, 90, 0.8)",
        borderWidth: 2,
        type: "line",
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { color: theme.palette.text.primary } },
      tooltip: {
        callbacks: {
          label: ctx => {
            const item = dataList[ctx.dataIndex];
            return [`Frames: ${item.frame_count}`, `Status: ${item.comparison}`];
          },
        },
      },
      title: {
        display: true,
        text: "Camera Frame Counts Compared to Overall Average",
        color: theme.palette.text.primary,
      },
      datalabels: {
        color: theme.palette.text.primary,
        anchor: "end",
        align: "end",
        font: { weight: "bold" },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { color: theme.palette.text.primary } , title: { display: true, text: "Frames Count" } },
      y: { ticks: { color: theme.palette.text.primary }, title: { display: true, text: "Camera Name" }},
    },
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
      ) : (
        <Box sx={{ height: 500 }}>
          <Bar data={data} options={options} plugins={[ChartDataLabels]} />
        </Box>
      )}
    </Card>
  );
};

export default CameraFrameComparisonChart;
